/* eslint-disable react/prop-types */
import { Icon, Tooltip } from "@mui/material";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import { getUproColor } from "utils/colors";
import { makeNiceText } from "utils/formatters";

const getRows = (data, setSelected, idProveedor, colores) => {
  const propsSegunEstado = (estado) => {
    switch (estado) {
      case "Programada":
        return {
          sx: {
            cursor: "default",
          },
        };

      default:
        return {
          sx: {
            cursor: "pointer",
          },
        };
    }
  };

  const rows = [];
  if (data) {
    data.forEach((item, index) => {
      const licitacionFin = item?.licitacionFin ? dayjs(item?.licitacionFin) : null;
      const licitacionInicio = item?.licitacionInicio ? dayjs(item?.licitacionInicio) : null;

      rows.push({
        hasBorder: index !== data.length - 1,
        onClick: item.licitacionEstado !== "Programada" ? () => setSelected(item) : undefined,
        ariaLabel:
          item.licitacionEstado !== "Programada"
            ? `Ver cotización ${item.id}: ${item.nombre || `Pedido n° ${item.id}`}`
            : undefined,
        type: (
          <SoftBox
            display="flex"
            alignItems="center"
            {...propsSegunEstado(item.licitacionEstado)}
            data={
              <Icon
                sx={{
                  verticalAlign: "middle",
                  color: getUproColor("uproBlue"),
                }}
                fontSize="lg"
              >
                receipt
              </Icon>
            }
          >
            <SoftBox pl={3} mr={2}>
              {licitacionInicio && licitacionInicio.isBefore(dayjs()) && (
                <Tooltip title='Cotización "En curso" haga click para ver oferta' placement="top">
                  <Icon
                    sx={{
                      verticalAlign: "middle",
                      color: getUproColor("uproBlue"),
                    }}
                    fontSize="lg"
                  >
                    arrow_outward
                  </Icon>
                </Tooltip>
              )}
            </SoftBox>
          </SoftBox>
        ),
        id: (
          <SoftBox {...propsSegunEstado(item.licitacionEstado)} data={item?.id}>
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="info"
              fontWeight="bold"
            >
              {item?.id}
            </SoftTypography>
          </SoftBox>
        ),
        nombre: (
          <SoftBox
            {...propsSegunEstado(item.licitacionEstado)}
            data={makeNiceText(item?.nombre, "nombre")}
          >
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="dark"
              fontWeight="medium"
            >
              {item?.nombre ? makeNiceText(item?.nombre, "nombre") : "Pedido nº " + item?.id}
            </SoftTypography>
          </SoftBox>
        ),
        rubros: (
          <SoftBox
            {...propsSegunEstado(item.licitacionEstado)}
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            gap={0.5}
            sx={{ minWidth: 180, maxWidth: 300 }}
          >
            {item?.categorias?.slice(0, 4).map((rubro) => (
              <SoftBox
                key={rubro.nombre}
                display="flex"
                alignItems="center"
                gap={0.5}
                px={0.8}
                py={0.35}
                sx={{ border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#ffffff" }}
              >
                <SoftBox sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "info.main", flex: "0 0 auto" }} />
                <SoftTypography variant="caption" color="dark" fontWeight="medium" sx={{ lineHeight: 1.2 }}>
                  {rubro.nombre}
                </SoftTypography>
              </SoftBox>
            ))}
            {item?.categorias?.length > 4 && (
              <SoftBox px={0.8} py={0.35} sx={{ borderRadius: "8px", backgroundColor: "#f4f6f8" }}>
                <SoftTypography variant="caption" color="text" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                  +{item.categorias.length - 4}
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        ),
        licitacionInicio: (
          <SoftBox {...propsSegunEstado(item.licitacionEstado)}>
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="dark"
              fontWeight="medium"
            >
              {item?.licitacionInicio
                ? dayjs(item?.licitacionInicio).format("DD/MM/YYYY HH:mm")
                : "-"}
            </SoftTypography>
          </SoftBox>
        ),
        licitacionFin: (
          <SoftBox {...propsSegunEstado(item.licitacionEstado)}>
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="dark"
              fontWeight="medium"
            >
              {item?.licitacionFin ? dayjs(item?.licitacionFin).format("DD/MM/YYYY HH:mm") : "-"}
            </SoftTypography>
          </SoftBox>
        ),
        licitacionEstado: (
          <SoftBox
            {...propsSegunEstado(item.licitacionEstado)}
            display="flex"
            flexDirection="row"
            justifyContent="center"
          >
            <SoftBadge
              badgeContent={item.licitacionEstado}
              color={colores[item.licitacionEstado]}
            />
          </SoftBox>
        ),
        estadoProveedorLicitacion: (
          <SoftBox
            {...propsSegunEstado(item.licitacionEstado)}
            display="flex"
            flexDirection="row"
            justifyContent="center"
          >
            {item.provisiones?.filter((i) => !i.anulada).length > 0 ? (
              <SoftBox pl={1}>
                <SoftBadge badgeContent="Ganado" color="uproGreen" />
              </SoftBox>
            ) : item?.cotizaciones?.find((cotizacion) => cotizacion.idProveedor === idProveedor) ? (
              <SoftBox pl={1}>
                <SoftBadge badgeContent="Licitado" color="uproBlue" />
              </SoftBox>
            ) : (
              <SoftBox pl={1}>
                <SoftBadge badgeContent="Sin cotizar" color="secondary" />
              </SoftBox>
            )}
          </SoftBox>
        ),
      });
    });
  }
  return rows;
};

const columns = [
  { name: "id", desc: "ID", width: "1%", align: "left" },
  { name: "nombre", desc: "Nombre", align: "left" },
  { name: "rubros", desc: "Rubros", align: "left" },
  { name: "licitacionInicio", desc: "Fecha Inicio", align: "left" },
  { name: "licitacionFin", desc: "Fecha Fin", align: "left" },
  { name: "licitacionEstado", desc: "cotización", align: "center", noOrder: true },
  { name: "estadoProveedorLicitacion", desc: "Estado", align: "center", noOrder: true },
  { name: "type", desc: " ", align: "left", width: "1%", noOrder: true },
];

export default { columns, getRows };
